import React, { useState, useEffect } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    Grid,
    FormControl,
    InputLabel,
    Select,
    Box,
    Typography,
    Autocomplete,
    CircularProgress
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { moduleService } from '../services/moduleService';
import api, { jiraStoriesAPI, releasesAPI } from '../services/api';
import { getReleaseScopes } from '../services/releaseManagementApi';
import { useAuth } from '../context/AuthContext';
import RichTextEditor from './RichTextEditor';

const IssueDetail = ({ open, onClose, onSave, onDelete, issue, defaultReleaseId, defaultStoryId }) => {
    const { user } = useAuth();
    const [formData, setFormData] = useState({
        title: '',
        description: '',
        status: 'Open',
        priority: 'Medium',
        severity: 'Major',
        module_id: '',
        sub_module_id: '',
        feature_id: '',
        assigned_to: '', // Internal user ID (legacy/optional)
        jira_assignee_id: '', // Jira Account ID
        jira_assignee_name: '', // Display name for UI
        jira_assignee_email: '', // Email for Slack notifications
        video_url: '',
        screenshot_urls: '', // Stored as newline separated string in UI
        reporter_name: '',
        jira_story_id: '',
        release_id: '',
        scope_id: '',
        videoFile: null,
        screenshotFiles: []
    });

    const [modules, setModules] = useState([]);
    const [subModules, setSubModules] = useState([]);
    const [features, setFeatures] = useState([]);
    const [releases, setReleases] = useState([]);
    const [scopes, setScopes] = useState([]);
    const [jiraUsers, setJiraUsers] = useState([]);
    const [loadingJiraUsers, setLoadingJiraUsers] = useState(false);
    const [jiraUserQuery, setJiraUserQuery] = useState('');
    const [uploading, setUploading] = useState(false);
    const [preloadedMediaUrls, setPreloadedMediaUrls] = useState([]);

    useEffect(() => {
        if (open) {
            fetchDependencies();
            if (issue) {
                setFormData({
                    title: issue.title,
                    description: issue.description || '',
                    status: issue.status,
                    priority: issue.priority,
                    severity: issue.severity,
                    module_id: issue.module_id || '',
                    sub_module_id: issue.sub_module_id || '',
                    feature_id: issue.feature_id || '',
                    assigned_to: issue.assigned_to || '',
                    jira_assignee_id: issue.jira_assignee_id || '',
                    jira_assignee_name: issue.jira_assignee_id ? 'Loading...' : '',
                    jira_assignee_email: issue.jira_assignee_email || '',
                    video_url: issue.video_url || '',
                    screenshot_urls: issue.screenshot_urls || '',
                    reporter_name: issue.reporter_name || issue.creator?.full_name || '',
                    jira_story_id: issue.jira_story_id || '',
                    release_id: issue.release_id || defaultReleaseId || '',
                    scope_id: issue.scope_id || '',
                    videoFile: null,
                    screenshotFiles: []
                });
            } else {
                // Reset form for new issue
                setFormData({
                    title: '',
                    description: '',
                    status: 'Open',
                    priority: 'Medium',
                    severity: 'Major',
                    module_id: '',
                    sub_module_id: '',
                    feature_id: '',
                    assigned_to: '',
                    jira_assignee_id: '',
                    jira_assignee_name: '',
                    jira_assignee_email: '',
                    video_url: '',
                    screenshot_urls: '',
                    reporter_name: user?.full_name || user?.email || 'Unknown',
                    jira_story_id: defaultStoryId || '',
                    release_id: defaultReleaseId || '',
                    scope_id: '',
                    videoFile: null,
                    screenshotFiles: []
                });
            }
        } else {
            // Reset form when dialog closes
            setFormData({
                title: '',
                description: '',
                status: 'Open',
                priority: 'Medium',
                severity: 'Major',
                module_id: '',
                sub_module_id: '',
                feature_id: '',
                assigned_to: '',
                jira_assignee_id: '',
                jira_assignee_name: '',
                jira_assignee_email: '',
                video_url: '',
                screenshot_urls: '',
                reporter_name: '',
                jira_story_id: '',
                release_id: '',
                scope_id: '',
                videoFile: null,
                screenshotFiles: []
            });
        }
    }, [open, issue, user, defaultStoryId, defaultReleaseId]);

    // Fetch scopes when release changes
    useEffect(() => {
        const fetchScopes = async () => {
            if (!formData.release_id) {
                setScopes([]);
                return;
            }
            try {
                const response = await getReleaseScopes(formData.release_id);
                setScopes(response.data || []);
            } catch (error) {
                console.error('Error fetching scopes:', error);
                setScopes([]);
            }
        };
        if (open) fetchScopes();
    }, [formData.release_id, open]);

    // Pre-fetch existing media as blob URLs so they render inline in the editor
    useEffect(() => {
        let cancelled = false;
        const blobUrls = [];

        const preload = async () => {
            if (!issue?.id) { setPreloadedMediaUrls([]); return; }

            // Only preload images — videos are loaded on-demand by AuthenticatedVideo
            const rawUrls = [
                ...(issue.screenshot_urls ? issue.screenshot_urls.split('\n').filter(Boolean) : []),
            ];
            if (rawUrls.length === 0) { setPreloadedMediaUrls([]); return; }

            const results = await Promise.all(
                rawUrls.map(async (url) => {
                    try {
                        const proxyUrl = `/issues/${issue.id}/media-proxy?url=${encodeURIComponent(url)}`;
                        const res = await api.get(proxyUrl, { responseType: 'blob', timeout: 60000 });
                        const blobUrl = URL.createObjectURL(res.data);
                        blobUrls.push(blobUrl);
                        return blobUrl;
                    } catch { return null; }
                })
            );
            if (!cancelled) setPreloadedMediaUrls(results.filter(Boolean));
        };

        if (open && issue) preload();
        else setPreloadedMediaUrls([]);

        return () => {
            cancelled = true;
            blobUrls.forEach(u => URL.revokeObjectURL(u));
        };
    }, [open, issue]); // eslint-disable-line react-hooks/exhaustive-deps

    // Debounced search for Jira users
    useEffect(() => {
        const timer = setTimeout(() => {
            if (jiraUserQuery.length >= 2) {
                searchJiraUsers(jiraUserQuery);
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [jiraUserQuery]);

    const fetchDependencies = async () => {
        try {
            const modulesData = await moduleService.getAll();
            setModules(modulesData);
            // Fetch sub-modules, features, and releases
            const [subModulesRes, featuresRes, releasesData] = await Promise.all([
                api.get('/sub-modules/'),
                api.get('/features/'),
                releasesAPI.getAll(),
            ]);
            setSubModules(subModulesRes.data);
            setFeatures(featuresRes.data);
            setReleases(releasesData);
        } catch (error) {
            console.error('Error fetching dependencies:', error);
        }
    };

    const searchJiraUsers = async (query) => {
        setLoadingJiraUsers(true);
        try {
            const users = await jiraStoriesAPI.searchUsers(query);
            setJiraUsers(users);
        } catch (error) {
            console.error('Error searching Jira users:', error);
        } finally {
            setLoadingJiraUsers(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const updated = { ...prev, [name]: value };
            // Cascade: clear sub_module when module changes
            if (name === 'module_id') {
                updated.sub_module_id = '';
                updated.feature_id = '';
            }
            // Cascade: clear feature when sub_module changes
            if (name === 'sub_module_id') {
                updated.feature_id = '';
            }
            // Clear scope when release changes
            if (name === 'release_id') {
                updated.scope_id = '';
            }
            return updated;
        });
    };

    const handleJiraAssigneeChange = (event, newValue) => {
        if (newValue) {
            setFormData(prev => ({
                ...prev,
                jira_assignee_id: newValue.accountId,
                jira_assignee_name: newValue.displayName,
                jira_assignee_email: newValue.emailAddress || ''
            }));
        } else {
            setFormData(prev => ({
                ...prev,
                jira_assignee_id: '',
                jira_assignee_name: '',
                jira_assignee_email: ''
            }));
        }
    };

    const handleSubmit = async () => {
        // Separate files from regular data
        const { videoFile, screenshotFiles, jira_assignee_name, ...rawData } = formData;

        // Sanitize data: Convert empty strings to null for integer fields
        const issueData = {
            ...rawData,
            assigned_to: rawData.assigned_to === '' ? null : rawData.assigned_to,
            module_id: rawData.module_id === '' ? null : rawData.module_id,
            sub_module_id: rawData.sub_module_id === '' ? null : rawData.sub_module_id,
            feature_id: rawData.feature_id === '' ? null : rawData.feature_id,
            release_id: rawData.release_id === '' ? null : rawData.release_id,
            scope_id: rawData.scope_id === '' ? null : rawData.scope_id,
        };

        // Validate: release is mandatory
        if (!issueData.release_id) {
            alert('Please select a Release before saving.');
            return;
        }

        // 1. Save Issue Data first
        try {
            setUploading(true);
            await onSave(issueData, { video: videoFile, screenshots: screenshotFiles });

        } catch (error) {
            console.error("Error saving issue:", error);
        } finally {
            setUploading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{issue ? 'Edit Issue' : 'New Issue'}</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                        name="title"
                        label="Title"
                        value={formData.title}
                        onChange={handleChange}
                        fullWidth
                        required
                    />

                    <Box>
                        <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
                            Description *
                        </Typography>
                        <RichTextEditor
                            key={`editor-${issue?.id || 'new'}-${preloadedMediaUrls.length}`}
                            value={formData.description}
                            onChange={(html, mediaFiles) => {
                                setFormData(prev => ({
                                    ...prev,
                                    description: html,
                                    videoFile: mediaFiles.find(f => f.type.startsWith('video/')) || prev.videoFile,
                                    screenshotFiles: mediaFiles.filter(f => f.type.startsWith('image/'))
                                }));
                            }}
                            placeholder="Describe the issue with inline media support..."
                            existingMedia={preloadedMediaUrls}
                        />
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    name="status"
                                    value={formData.status}
                                    label="Status"
                                    onChange={handleChange}
                                >
                                    <MenuItem value="Open">Open</MenuItem>
                                    <MenuItem value="In Development">In Development</MenuItem>
                                    <MenuItem value="Resolved">Resolved</MenuItem>
                                    <MenuItem value="Fix Deployed">Fix Deployed</MenuItem>
                                    <MenuItem value="Testing in progress">Testing in progress</MenuItem>
                                    <MenuItem value="Fix not working">Fix not working</MenuItem>
                                    <MenuItem value="Closed">Closed</MenuItem>
                                    <MenuItem value="Backlog">Backlog</MenuItem>
                                    <MenuItem value="Defaulter">Defaulter</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Priority</InputLabel>
                                <Select
                                    name="priority"
                                    value={formData.priority}
                                    label="Priority"
                                    onChange={handleChange}
                                >
                                    <MenuItem value="Low">Low</MenuItem>
                                    <MenuItem value="Medium">Medium</MenuItem>
                                    <MenuItem value="High">High</MenuItem>
                                    <MenuItem value="Critical">Critical</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Severity</InputLabel>
                                <Select
                                    name="severity"
                                    value={formData.severity}
                                    label="Severity"
                                    onChange={handleChange}
                                >
                                    <MenuItem value="Minor">Minor</MenuItem>
                                    <MenuItem value="Major">Major</MenuItem>
                                    <MenuItem value="Critical">Critical</MenuItem>
                                    <MenuItem value="Blocker">Blocker</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required error={!formData.release_id}>
                                <InputLabel>Release *</InputLabel>
                                <Select
                                    name="release_id"
                                    value={formData.release_id}
                                    label="Release *"
                                    onChange={handleChange}
                                >
                                    {releases.map(r => (
                                        <MenuItem key={r.id} value={r.id}>
                                            {r.version}{r.name ? ` — ${r.name}` : ''}
                                            {r.overall_status === 'in_progress' ? ' (Active)' : ''}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Module</InputLabel>
                                <Select
                                    name="module_id"
                                    value={formData.module_id}
                                    label="Module"
                                    onChange={handleChange}
                                >
                                    {modules.map(module => (
                                        <MenuItem key={module.id} value={module.id}>
                                            {module.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Sub Module</InputLabel>
                                <Select
                                    name="sub_module_id"
                                    value={formData.sub_module_id}
                                    label="Sub Module"
                                    onChange={handleChange}
                                    disabled={!formData.module_id}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {subModules
                                        .filter(sm => sm.module_id === formData.module_id)
                                        .map(sm => (
                                            <MenuItem key={sm.id} value={sm.id}>
                                                {sm.name}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Feature</InputLabel>
                                <Select
                                    name="feature_id"
                                    value={formData.feature_id}
                                    label="Feature"
                                    onChange={handleChange}
                                    disabled={!formData.sub_module_id}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {features
                                        .filter(f => f.sub_module_id === formData.sub_module_id)
                                        .map(f => (
                                            <MenuItem key={f.id} value={f.id}>
                                                {f.name}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth>
                                <InputLabel>Scope</InputLabel>
                                <Select
                                    name="scope_id"
                                    value={formData.scope_id}
                                    label="Scope"
                                    onChange={handleChange}
                                    disabled={!formData.release_id}
                                >
                                    <MenuItem value=""><em>None</em></MenuItem>
                                    {scopes.map(scope => (
                                        <MenuItem key={scope.id} value={scope.id}>
                                            {scope.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    </Grid>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <TextField
                                label="Reporter"
                                value={formData.reporter_name}
                                fullWidth
                                disabled
                                helperText="Automatically set to current user"
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Autocomplete
                                options={jiraUsers}
                                getOptionLabel={(option) => option.displayName || ''}
                                loading={loadingJiraUsers}
                                onInputChange={(event, newInputValue) => {
                                    setJiraUserQuery(newInputValue);
                                }}
                                onChange={handleJiraAssigneeChange}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Jira Assignee"
                                        fullWidth
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <React.Fragment>
                                                    {loadingJiraUsers ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </React.Fragment>
                                            ),
                                        }}
                                    />
                                )}
                            />
                        </Grid>
                    </Grid>

                    {formData.jira_story_id && (
                        <TextField
                            label="Linked Story ID"
                            value={formData.jira_story_id}
                            fullWidth
                            disabled
                        />
                    )}

                </Box>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
                {issue && (
                    <Button
                        onClick={() => {
                            if (window.confirm('Are you sure you want to delete this issue?')) {
                                onDelete(issue.id);
                            }
                        }}
                        color="error"
                        startIcon={<DeleteIcon />}
                    >
                        Delete
                    </Button>
                )}
                <Box>
                    <Button onClick={onClose} sx={{ mr: 1 }}>Cancel</Button>
                    <Button onClick={handleSubmit} variant="contained" color="primary" disabled={uploading}>
                        {uploading ? 'Uploading...' : 'Save & Upload'}
                    </Button>
                </Box>
            </DialogActions>
        </Dialog>
    );
};

export default IssueDetail;
